import {Component, ElementRef, EventEmitter, Input, Output} from '@angular/core';
import {ModalComponent} from '../../../core/components/modal/modal.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ToastrService} from 'ngx-toastr';
import {User} from '../../../core/entities/user/user.entity';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {environment} from '../../../../environments/environment';
import { QRCodeComponent } from 'angularx-qrcode';

@Component({
  selector: 'app-share-profile-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslocoPipe,
    QRCodeComponent,
  ],
  templateUrl: './share-profile-modal.html',
  styleUrl: './share-profile-modal.scss'
})
export class ShareProfileModal {
  @Input() open = false;
  @Output() close = new EventEmitter<void>();

  public user: User | null;
  public url: string;

  public constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService
  ) {
    this.user = this.authenticationService.getCurrentUser();
    this.url = this.getUrl();
  }

  public copyUrl() {
    if (!this.url) {
      this.toastr.error(
        this.transloco.translate('share-profile-modal.errors.copy-url.content'),
        this.transloco.translate('share-profile-modal.errors.copy-url.title'),
      );
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      this.toastr.error(
        this.transloco.translate('share-profile-modal.errors.copy-url.content'),
        this.transloco.translate('share-profile-modal.errors.copy-url.title'),
      );
      return;
    }

    navigator.clipboard.writeText(this.url)
      .then(() => {
        this.toastr.success(
          this.transloco.translate('share-profile-modal.success.copy-url.content'),
          this.transloco.translate('share-profile-modal.success.copy-url.title'),
        );
      })
      .catch(() => {
        this.toastr.error(
          this.transloco.translate('share-profile-modal.errors.copy-url.content'),
          this.transloco.translate('share-profile-modal.errors.copy-url.title'),
        );
      });
  }

  private getUrl(): string {
    if (!this.user) {
      return '';
    }

    const baseUrl = environment.frontBaseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '');
    if (!baseUrl) {
      return '';
    }

    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const shareUrl = new URL(`photographer/${this.user.slug}`, normalizedBase);
    shareUrl.searchParams.set('utm_source', 'creator');
    shareUrl.searchParams.set('utm_medium', 'share-profile-modal');
    return shareUrl.toString();
  }

  public downloadQrAsPng(hostRefOrEl: ElementRef<HTMLElement> | HTMLElement, scale = 4) {
    try {
      if (!this.url || typeof window === 'undefined') return;

      const host: HTMLElement =
        hostRefOrEl instanceof ElementRef ? hostRefOrEl.nativeElement : hostRefOrEl;

      const svg: SVGElement | null = host.querySelector('svg');
      if (!svg) throw new Error('QR SVG not found');

      // Serialize SVG to data URL
      const xml = new XMLSerializer().serializeToString(svg);
      const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);

      const img = new Image();
      img.onload = () => {
        const w = img.width * scale;
        const h = img.height * scale;

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');

        ctx.fillStyle = '#FFFFFF'; // background blanc (meilleure lisibilité)
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const pngUrl = canvas.toDataURL('image/png');

        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = this.buildQrFilename('png');
        document.body.appendChild(a);
        a.click();
        a.remove();

        this.toastr.success(
          this.transloco.translate('share-profile-modal.success.download.content'),
          this.transloco.translate('share-profile-modal.success.download.title'),
        );
      };
      img.onerror = () => {
        this.toastr.error(
          this.transloco.translate('share-profile-modal.errors.download.content'),
          this.transloco.translate('share-profile-modal.errors.download.title'),
        );
      };
      img.src = svgDataUrl;
    } catch (e) {
      console.error(e);
      this.toastr.error(
        this.transloco.translate('share-profile-modal.errors.download.content'),
        this.transloco.translate('share-profile-modal.errors.download.title'),
      );
    }
  }

  /** Build a helpful filename like 'shutterflow-qr-<slug>.ext' */
  private buildQrFilename(ext: 'svg' | 'png'): string {
    const slug = this.user?.slug ?? 'profile';
    return `shutterflow-qr-${slug}.${ext}`;
  }

  public closeModal() {
    this.close.emit();
  }
}
